import { useEffect, useState } from "react";
import LoadingDisplay from "../common/LoadingDisplay";
import ErrorDisplay from "../common/ErrorDisplay";
import { Col, Row } from "react-bootstrap";
import {
  drawingApi,
  DrawingTypeModel,
  LayerNodeModel,
  LayerNodeTypeModel,
} from "../api/drawingApi";
import { Problem } from "../api/backendApi";
import {
  copyTreeNode,
  findTreeNode,
  findTreeNodeParent,
  getFlattenedTree,
  Tree,
  TreeNode,
} from "./Tree";
import AttributeWindowEditModal from "./AttributeWindowEditModal";
import ContextMenu, {
  ContextMenuOption,
  ContextMenuSeparatorText,
} from "../components/contextmenus/ContextMenu";
import StyleEditModal from "./StyleEditModal";
import LayerEditModal from "./LayerEditModal";
import {
  CheckCircle,
  ExclamationDiamond,
  IconProps,
  JournalRichtext,
  Layers,
  Wrench,
  XCircle,
} from "react-bootstrap-icons";
import InvalidComponentEditModal from "./InvalidComponentEditModal";
import { DrawingContext } from "./DrawingContext";

const ValidInformationWindowNames = [
  "DEACTIVATE",
  "DEACTIVATE-RHD",
  "FIRE",
  "INTROVIEW",
  "RECOVERY",
  "WATER",
];

enum NodeType {
  AttributeWindowRoot,
  AttributeWindow,
  InformationWindowRoot,
  InformationWindow,
  Style,
  StyleRoot,
  Layer,
  LayerComponent,
  LayerRoot,
}

interface NodeData {
  type: NodeType;
  done?: boolean;
}

function nodeDataEquals(a: NodeData | undefined, b: NodeData | undefined) {
  return a?.type === b?.type;
}

interface TemplateEditProps {
  drawingName: string;
  readOnly: boolean;
  onExport?: () => void;
  onMpfzPublishableChange?: (isPublishable: boolean) => void;
  onMissingTranslationsChange?: (isMissingTranslations: boolean) => void;
  onCheckedTextsChange?: (hasCheckedTexts: boolean) => void;
}

function TemplateEdit({
  drawingName,
  readOnly,
  onCheckedTextsChange,
}: TemplateEditProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Problem | string>();
  const [rootNode, setRootNode] = useState<TreeNode<NodeData>>({
    text: "root",
    children: [],
  });
  const [showAttributeWindowsModal, setShowAttributeWindowsModal] =
    useState(false);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [showLayerModal, setShowLayerModal] = useState(false);
  const [showInvalidComponentModal, setShowInvalidComponentModal] =
    useState(false);
  const [contextMenuOptions, setContextMenuOptions] = useState<
    ContextMenuOption[]
  >([]);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const [editNode, setEditNode] = useState<TreeNode<NodeData>>();
  const [editParentNodeName, setEditParentNodeName] = useState<string>();
  const [focusedNode, setFocusedNode] = useState<TreeNode<NodeData>>();
  const [layerIsMoving, setLayerIsMoving] = useState(false);
  const [attributeWindowNameOptions, setAttributeWindowNameOptions] =
    useState<string[]>();

  useEffect(() => {
    async function loadData() {
      setLoading(true);

      const result = await drawingApi.getDrawingTree(
        drawingName,
        DrawingTypeModel.Template,
      );

      setLoading(false);

      if (result.ok) {
        setError(undefined);

        const drawing = result.value;

        let rootNode: TreeNode<NodeData> = {
          text: "root",
          children: [],
        };

        rootNode.children?.push({
          text: "Styles",
          expanded: false,
          data: { type: NodeType.StyleRoot },
          children: drawing.styles.map((s) => ({
            text: s,
            data: { type: NodeType.Style },
          })),
        });

        let walkedNodes: (string | undefined)[] = [];

        const convertToTree = (
          layers: LayerNodeModel[],
          parentName: string | undefined,
        ): TreeNode<NodeData>[] => {
          if (walkedNodes.indexOf(parentName) >= 0) {
            setError(
              `There's a circular reference in the layers that make ${
                parentName ?? "<null>"
              } appear twice. Some information might be missing.`,
            );
            return [];
          }

          walkedNodes.push(parentName);

          // also ensure undefined == null...
          const currentLayers = layers.filter((l) =>
            parentName ? l.parentNodeName === parentName : !l.parentNodeName,
          );

          if (currentLayers.length === 0) {
            return [];
          }

          return currentLayers.map((l) => ({
            text: l.name,
            data: {
              type:
                l.type === LayerNodeTypeModel.Layer
                  ? NodeType.Layer
                  : NodeType.LayerComponent,
            },
            children:
              l.type === LayerNodeTypeModel.Layer
                ? convertToTree(layers, l.name)
                : [],
          }));
        };

        rootNode.children?.push({
          text: "Drawing",
          expanded: true,
          data: { type: NodeType.LayerRoot },
          children: convertToTree(drawing.layerNodes, undefined),
        });

        rootNode.children?.push({
          text: "Information Windows",
          expanded: true,
          data: { type: NodeType.InformationWindowRoot },
          children: drawing.informationWindows.map((iw) => ({
            text: iw.name,
            data: {
              type: NodeType.InformationWindow,
              done: iw.done,
            },
          })),
        });

        setRootNode(rootNode);
      } else {
        setError(result.problem);
      }
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingName]);

  const toggleExpanded = (node: TreeNode<NodeData>) => {
    const modified = copyTreeNode(rootNode, (n) => {
      if (n === node) {
        return { ...n, expanded: !n.expanded };
      }

      return n;
    });

    setRootNode(modified);
  };

  const checkIfNodeCanMove = (node: TreeNode<NodeData>): [boolean, boolean] => {
    let allowUp = false;
    let allowDown = false;

    const parentNode = findTreeNodeParent(node, rootNode, nodeDataEquals);

    if (
      parentNode &&
      parentNode.children &&
      (parentNode.data?.type === NodeType.Layer ||
        parentNode.data?.type === NodeType.LayerComponent ||
        parentNode.data?.type === NodeType.LayerRoot)
    ) {
      // these can be moved up or down
      const index = parentNode.children.findIndex(
        (n) => n.text === node.text && n.data?.type === node.data?.type,
      );

      allowUp = index > 0;
      allowDown = index < parentNode.children.length - 1;
    }

    return [allowUp, allowDown];
  };

  const getInformationWindowsThatAreNotPresent = () => {
    const informationWindowsNode = findTreeNode(
      rootNode,
      (n) => n.data?.type === NodeType.InformationWindowRoot,
    );
    if (
      !informationWindowsNode ||
      !informationWindowsNode.children ||
      informationWindowsNode.children.length === 0
    )
      return [];

    var currentInformationWindows = informationWindowsNode.children.map(
      (iw) => iw.text,
    );

    return ValidInformationWindowNames.filter(
      (iw) => currentInformationWindows.indexOf(iw) < 0,
    );
  };

  const contextMenu = (
    node: TreeNode<NodeData>,
    positionX: number,
    positionY: number,
  ): boolean => {
    if (focusedNode?.text !== node.text || focusedNode?.data !== node.data) {
      setFocusedNode(node);
    }

    var newContextMenuOptions: ContextMenuOption[] = [];

    if (node.data?.type === NodeType.Layer) {
      newContextMenuOptions.push({
        text: readOnly ? "View" : "Edit",
        data: () => editLayerNode(node),
      });
      if (!readOnly) {
        newContextMenuOptions.push({
          text: "Remove",
          data: () => removeLayerNode(node),
        });
      }
    }

    if (
      !readOnly &&
      (node.data?.type === NodeType.LayerRoot ||
        node.data?.type === NodeType.Layer)
    ) {
      newContextMenuOptions.push({
        text: "Add layer",
        data: () => addLayerNode(node),
      });
    }

    if (
      !readOnly &&
      (node.data?.type === NodeType.Layer ||
        node.data?.type === NodeType.LayerComponent)
    ) {
      const [allowUp, allowDown] = checkIfNodeCanMove(node);

      if (newContextMenuOptions.length > 0 && (allowUp || allowDown))
        newContextMenuOptions.push({ text: ContextMenuSeparatorText });

      if (allowUp)
        newContextMenuOptions.push({
          text: "Move up",
          data: () => moveLayerNode(node, -1),
        });

      if (allowDown)
        newContextMenuOptions.push({
          text: "Move down",
          data: () => moveLayerNode(node, 1),
        });
    }

    if (node.data?.type === NodeType.Style) {
      newContextMenuOptions.push({
        text: readOnly ? "View" : "Edit",
        data: () => editStyleNode(node),
      });

      if (!readOnly) {
        newContextMenuOptions.push({
          text: "Remove",
          data: () => removeStyleNode(node),
        });
      }
    }

    if (!readOnly && node.data?.type === NodeType.StyleRoot) {
      newContextMenuOptions.push({
        text: "Add style",
        data: () => addStyleNode(),
      });
    }

    if (!readOnly && node.data?.type === NodeType.AttributeWindowRoot) {
      newContextMenuOptions.push({
        text: "Add attribute window",
        data: () => addAttributeWindowNode(node),
      });
    }

    if (!readOnly && node.data?.type === NodeType.InformationWindowRoot) {
      if (getInformationWindowsThatAreNotPresent().length > 0) {
        newContextMenuOptions.push({
          text: "Add information window",
          data: () => addAttributeWindowNode(node),
        });
      }
    }

    if (
      node.data?.type === NodeType.AttributeWindow ||
      node.data?.type === NodeType.InformationWindow
    ) {
      newContextMenuOptions.push({
        text: readOnly ? "View" : "Edit",
        data: () => editAttributeWindowNode(node),
      });

      if (!readOnly) {
        newContextMenuOptions.push({
          text: "Remove",
          data: () => removeAttributeWindowNode(node),
        });
      }
    }

    setContextMenuOptions(newContextMenuOptions);
    setContextMenuPosition({ x: positionX, y: positionY });

    return newContextMenuOptions.length > 0;
  };

  const removeTreeNodeByType = (text: string, type: NodeType) => {
    const nodeToRemove = findTreeNode(
      rootNode,
      (n) => n.text === text && n.data?.type === type,
    );

    if (nodeToRemove) {
      removeTreeNode(nodeToRemove);
    }
  };

  const removeTreeNode = (node: TreeNode<NodeData>) => {
    const modifiedNodes = copyTreeNode(rootNode, (n) => ({
      ...n,
      children: n.children
        ? n.children.filter((c) => c.text !== node.text || c.data !== node.data)
        : n.children,
    }));

    setRootNode(modifiedNodes);
  };

  const removeAttributeWindowNode = async (node: TreeNode<NodeData>) => {
    if (
      !window.confirm("Are you sure you want to remove this Attribute Window?")
    )
      return;

    const response = await drawingApi.upsertAttributeWindow(
      drawingName,
      DrawingTypeModel.Template,
      node.text,
      node.data?.type === NodeType.InformationWindow,
      [],
    );

    if (response.ok) {
      setError(undefined);

      removeTreeNode(node);
    } else {
      setError(response.problem);
    }
  };

  const addAttributeWindowNode = (node: TreeNode<NodeData>) => {
    if (node.data?.type === NodeType.InformationWindowRoot) {
      var possibleOptions = getInformationWindowsThatAreNotPresent();

      if (possibleOptions.length === 0) {
        return;
      }
      setAttributeWindowNameOptions(possibleOptions);
    } else {
      setAttributeWindowNameOptions(undefined);
    }

    setFocusedNode(node);
    setEditNode(undefined);
    setShowAttributeWindowsModal(true);
  };

  const editAttributeWindowNode = (node: TreeNode<NodeData>) => {
    if (node.data?.type === NodeType.InformationWindow) {
      var possibleOptions = getInformationWindowsThatAreNotPresent();

      possibleOptions = [...possibleOptions, node.text ?? ""];
      possibleOptions.sort();
      setAttributeWindowNameOptions(possibleOptions);
    } else {
      setAttributeWindowNameOptions(undefined);
    }

    setFocusedNode(node);
    setEditNode(node);
    setShowAttributeWindowsModal(true);
  };

  const removeStyleNode = async (node: TreeNode<NodeData>) => {
    if (!window.confirm("Are you sure you want to remove this Style?")) return;

    const response = await drawingApi.removeStyle(
      drawingName,
      DrawingTypeModel.Template,
      node.text!,
    );

    if (response.ok) {
      setError(undefined);

      removeTreeNode(node);
    } else {
      setError(response.problem);
    }
  };

  const addStyleNode = () => {
    setEditNode(undefined);
    setShowStyleModal(true);
  };

  const editStyleNode = (node: TreeNode<NodeData>) => {
    setFocusedNode(node);
    setEditNode(node);
    setShowStyleModal(true);
  };

  const moveLayerNode = async (node: TreeNode<NodeData>, direction: number) => {
    // used toprevent race conditions
    setLayerIsMoving(true);

    const response = await drawingApi.moveLayer(
      drawingName,
      DrawingTypeModel.Template,
      node.text!,
      direction,
    );

    if (response.ok) {
      setError(undefined);

      setRootNode(
        copyTreeNode(rootNode, (n) => {
          if (n.children) {
            const childNodeIndex =
              n.children.findIndex(
                (c) => c.text === node.text && c.data?.type === node.data?.type,
              ) ?? -1;

            if (childNodeIndex >= 0) {
              const first = n.children[childNodeIndex];
              const second = n.children[childNodeIndex + direction];

              n.children[childNodeIndex] = second;
              n.children[childNodeIndex + direction] = first;
            }
          }

          return n;
        }),
      );
    } else {
      setError(response.problem);
    }
    setLayerIsMoving(false);
  };

  const removeLayerNode = async (node: TreeNode<NodeData>) => {
    if (!window.confirm("Are you sure you want to remove this Layer?")) return;

    const response = await drawingApi.removeLayer(
      drawingName,
      DrawingTypeModel.Template,
      node.text!,
    );

    if (response.ok) {
      setError(undefined);

      removeTreeNode(node);
    } else {
      setError(response.problem);
    }
  };

  const addLayerNode = async (parentNode: TreeNode<NodeData>) => {
    setFocusedNode(parentNode);
    setEditParentNodeName(parentNode.text);
    setEditNode(undefined);
    setShowLayerModal(true);
  };

  const editLayerNode = (node: TreeNode<NodeData>) => {
    setFocusedNode(node);
    setEditNode(node);
    setEditParentNodeName(
      findTreeNodeParent(node, rootNode, nodeDataEquals)?.text,
    );
    setShowLayerModal(true);
  };

  const addChildNode = (
    parentNode: TreeNode<NodeData>,
    name: string,
    type: NodeType,
    done: boolean | undefined,
  ) => {
    setRootNode(
      copyTreeNode(rootNode, (n) =>
        n === parentNode
          ? {
              ...n,
              focused: false,
              expanded: true,
              children: [
                ...(n.children ?? []),
                { text: name, focused: true, data: { type, done } },
              ],
            }
          : n,
      ),
    );
  };

  const updateNode = (
    node: TreeNode<NodeData>,
    newText: string,
    newDone: boolean | undefined,
  ) => {
    // without type we can bail because we can't do anything
    if (!node.data) return;

    // bail if no changes are required
    if (node.text === newText && node.data.done === newDone) return;

    const modified = copyTreeNode(rootNode, (n) => {
      if (n.text !== node.text || n.data?.type !== node.data?.type || !n.data)
        return { ...n };

      const modifiedNode: TreeNode<NodeData> = { ...n, data: { ...n.data } };
      modifiedNode.text = newText;
      modifiedNode.data!.done = newDone;

      return modifiedNode;
    });

    setRootNode(modified);
  };

  const handleKeyDown = (code: string, ctrlKey: boolean, altKey: boolean) => {
    // this will prevent tree changes while a move layer operation is running in the backgroung
    if (layerIsMoving) return false;

    const isUp = code === "ArrowUp" && !ctrlKey && !altKey;
    const isDown = code === "ArrowDown" && !ctrlKey && !altKey;
    const isLeft = code === "ArrowLeft" && !ctrlKey && !altKey;
    const isRight = code === "ArrowRight" && !ctrlKey && !altKey;
    const isMoveUp = code === "ArrowUp" && (ctrlKey || altKey);
    const isMoveDown = code === "ArrowDown" && (ctrlKey || altKey);

    if (!focusedNode) return false;

    if (isUp || isDown) {
      // easier to move things around like this
      const flatTree = getFlattenedTree(rootNode, true);

      const indexInTree = flatTree.findIndex(
        (n) =>
          n.text === focusedNode.text &&
          n.data?.type === focusedNode.data?.type,
      );

      if (indexInTree < 0) return false;

      let nodeToFocus: TreeNode<NodeData> | undefined;

      if (isUp) {
        if (indexInTree > 0) {
          nodeToFocus = flatTree[indexInTree - 1];
        }
      } else if (isDown) {
        if (indexInTree < flatTree.length - 1)
          nodeToFocus = flatTree[indexInTree + 1];
      }

      if (nodeToFocus) {
        setFocusedNode(nodeToFocus);

        return true;
      }
    } else if (isLeft || isRight) {
      let toggleExpand = false;

      if (
        isLeft &&
        focusedNode.expanded === true &&
        (focusedNode.children?.length ?? 0) > 0
      ) {
        toggleExpand = true;
      } else if (
        isRight &&
        focusedNode.expanded !== true &&
        (focusedNode.children?.length ?? 0) > 0
      ) {
        toggleExpand = true;
      }

      if (toggleExpand) {
        setRootNode(
          copyTreeNode(rootNode, (n) =>
            n.text === focusedNode.text &&
            n.data?.type === focusedNode.data?.type
              ? { ...n, expanded: !n.expanded }
              : n,
          ),
        );
        return true;
      }
    } else if (isMoveDown || isMoveUp) {
      const [allowUp, allowDown] = checkIfNodeCanMove(focusedNode);

      if (isMoveUp && allowUp) {
        moveLayerNode(focusedNode, -1);
        return true;
      } else if (isMoveDown && allowDown) {
        moveLayerNode(focusedNode, +1);
        return true;
      }
    }

    return false;
  };

  const handleInvalidComponent = (node: TreeNode<NodeData>) => {
    setShowInvalidComponentModal(true);
    setEditNode(node);
  };

  const getExtraElements = (node: TreeNode<NodeData>): JSX.Element => {
    let icons: JSX.Element[] = [];

    if (node.data) {
      if (node.data.type === NodeType.AttributeWindow) {
        const correspondingComponentNode = findTreeNode(
          rootNode,
          (n) =>
            n.data?.type === NodeType.LayerComponent && n.text === node.text,
        );

        if (!correspondingComponentNode)
          icons.push(
            <ExclamationDiamond
              key="invalid"
              color="red"
              role="button"
              title="Component is not present in the drawing."
              onClick={() => handleInvalidComponent(node)}
              className="mx-2"
            />,
          );
      }

      if (node.data.done === true) {
        icons.push(
          <CheckCircle
            key="done"
            color="green"
            title="It's done."
            className="mx-2"
          />,
        );
      } else if (node.data.done === false) {
        icons.push(
          <XCircle
            key="done"
            color="red"
            title="It's not done."
            className="mx-2"
          />,
        );
      }
    }

    return <>{icons}</>;
  };

  const getNodeIcon = (node: TreeNode<NodeData>): JSX.Element => {
    switch (node.data?.type as NodeType) {
      case NodeType.Layer:
        return <Layers />;
      case NodeType.Style:
        return <Transparency />;
      case NodeType.LayerComponent:
        return <Wrench />;
      case NodeType.AttributeWindow:
      case NodeType.InformationWindow:
        return <JournalRichtext />;
    }

    return <></>;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <DrawingContext.Provider
        value={{ name: drawingName, type: DrawingTypeModel.Template }}
      >
        <Row>
          <Col>
            <LoadingDisplay loading={loading} />
            <ErrorDisplay error={error} />
          </Col>
        </Row>
        <Row>
          <hr className="my-1"></hr>
        </Row>
        <Row
          className="align-items-top"
          style={{ flex: "1", overflow: "hidden" }}
        >
          <Col md="auto" style={{ height: "100%", overflow: "auto" }}>
            <Tree
              root={rootNode}
              onToggleExpanded={toggleExpanded}
              onFocus={setFocusedNode}
              onContextMenu={contextMenu}
              onKeyDown={handleKeyDown}
              isFocused={(n) =>
                n.text === focusedNode?.text &&
                n.data?.type === focusedNode?.data?.type
              }
              onRenderExtra={getExtraElements}
              onRenderIcon={getNodeIcon}
            />
          </Col>
        </Row>
        {showAttributeWindowsModal && (
          <AttributeWindowEditModal
            name={editNode?.text}
            isInformationWindow={
              focusedNode?.data?.type === NodeType.InformationWindowRoot ||
              editNode?.data?.type === NodeType.InformationWindow
            }
            nameOptions={attributeWindowNameOptions}
            onClose={() => setShowAttributeWindowsModal(false)}
            onSave={(name, done) => {
              onCheckedTextsChange && onCheckedTextsChange(false);
              editNode
                ? updateNode(editNode, name, done)
                : addChildNode(
                    focusedNode!,
                    name,
                    focusedNode?.data?.type === NodeType.AttributeWindowRoot
                      ? NodeType.AttributeWindow
                      : NodeType.InformationWindow,
                    done,
                  );
            }}
            readOnly={readOnly}
          ></AttributeWindowEditModal>
        )}
        {showStyleModal && (
          <StyleEditModal
            name={editNode?.text}
            onClose={() => setShowStyleModal(false)}
            onSave={(name) =>
              editNode
                ? updateNode(editNode, name, undefined)
                : addChildNode(focusedNode!, name, NodeType.Style, undefined)
            }
            readOnly={readOnly}
          ></StyleEditModal>
        )}
        {showLayerModal && (
          <LayerEditModal
            readOnly={readOnly}
            parentName={editParentNodeName ?? ""}
            name={editNode?.text}
            onClose={() => setShowLayerModal(false)}
            onSave={(name) =>
              editNode
                ? updateNode(editNode, name, undefined)
                : addChildNode(focusedNode!, name, NodeType.Layer, undefined)
            }
          />
        )}
        {showInvalidComponentModal && editNode && editNode.text && (
          <InvalidComponentEditModal
            name={editNode.text}
            otherWindows={getFlattenedTree(rootNode, false)
              .filter(
                (n) =>
                  n.data?.type === NodeType.AttributeWindow &&
                  n.text !== editNode.text,
              )
              .map((n) => n.text ?? "")
              .filter((text) => text !== "")}
            onClose={() => setShowInvalidComponentModal(false)}
            onRemove={(name) => removeTreeNodeByType(name, editNode.data!.type)}
          />
        )}
        {contextMenuOptions && contextMenuOptions.length > 0 && (
          <ContextMenu
            options={contextMenuOptions}
            positionX={contextMenuPosition.x}
            positionY={contextMenuPosition.y}
            onClick={(e) => {
              typeof (e.data === "function") && e.data();
              setContextMenuOptions([]);
            }}
            onClose={() => setContextMenuOptions([])}
          />
        )}
      </DrawingContext.Provider>
    </div>
  );
}

export default TemplateEdit;

// these are not available in the icon package at this moment
function Transparency(iconProps: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={iconProps.size ?? "1em"}
      height={iconProps.size ?? "1em"}
      viewBox="0 0 16 16"
    >
      <path d="M0 6.5a6.5 6.5 0 0 1 12.346-2.846 6.5 6.5 0 1 1-8.691 8.691A6.5 6.5 0 0 1 0 6.5m5.144 6.358a5.5 5.5 0 1 0 7.714-7.714 6.5 6.5 0 0 1-7.714 7.714m-.733-1.269q.546.226 1.144.33l-1.474-1.474q.104.597.33 1.144m2.614.386a5.5 5.5 0 0 0 1.173-.242L4.374 7.91a6 6 0 0 0-.296 1.118zm2.157-.672q.446-.25.838-.576L5.418 6.126a6 6 0 0 0-.587.826zm1.545-1.284q.325-.39.576-.837L6.953 4.83a6 6 0 0 0-.827.587l4.6 4.602Zm1.006-1.822q.183-.562.242-1.172L9.028 4.078q-.58.096-1.118.296l3.823 3.824Zm.186-2.642a5.5 5.5 0 0 0-.33-1.144 5.5 5.5 0 0 0-1.144-.33z" />
    </svg>
  );
}
